import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchStore } from '../stores/searchStore'

export default function SearchBar() {
  const { query, setQuery, search } = useSearchStore()
  const [localQuery, setLocalQuery] = useState(query)
  const [debouncing, setDebouncing] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const debouncedSearch = useCallback(
    (value: string) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      if (value.trim()) {
        setDebouncing(true)
      }
      timerRef.current = setTimeout(() => {
        setDebouncing(false)
        setQuery(value)
        search(value)
      }, 300)
    },
    [setQuery, search],
  )

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalQuery(value)
    debouncedSearch(value)
  }

  const handleClear = () => {
    setLocalQuery('')
    setQuery('')
    setDebouncing(false)
    if (timerRef.current) clearTimeout(timerRef.current)
    useSearchStore.getState().clearSearch()
  }

  return (
    <div className="relative">
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
        />
      </svg>
      <input
        type="text"
        value={localQuery}
        onChange={handleChange}
        placeholder="Search threads..."
        className="w-full pl-10 pr-10 py-2.5 bg-surface rounded-lg border border-border text-text placeholder:text-text-secondary focus:outline-none focus:border-accent text-sm"
      />
      {/* Right side: debounce spinner or clear button */}
      {localQuery && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
          {debouncing ? (
            <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          ) : (
            <button
              onClick={handleClear}
              className="text-text-secondary hover:text-text"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
